import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { stringify } from 'query-string';
import { replace } from 'redux-first-history';

import styled from 'styled-components';
import { SimplePopoverPlainOption, palette, FiltersPopover, FilterIcon } from '@dev/ee-components';
import dayjs from '@/services/tools/dayjs';
import { SHORT_DATE_FORMAT } from '@/services/constants';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import { ApplicationState as State } from '@/store';
import { useDateDropdownValue } from '@/services/hooks/useDateDropdownValue';
import useDateDropdownOptions from '@/services/hooks/useDateDropdownOptions';
import useMobile from '@/services/hooks/useMobile';
import { FiltersInterface } from '../../DeviceDetailsInterface';
import { campaignsOptions } from '../../config/getCampaignOptions';
import { channelsOptions } from '../../config/getChannelOptions';

const MobileFiltersContainer = styled.div`
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid ${palette.gray[3]};
    margin-left: 8px;
    padding: 11px 11px;
`;

const MobileFiltersPopover: FC<FiltersInterface> = (props: FiltersInterface): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const isMobile = useMobile();
    const { from, to, selectedCampaignName, selectedChannelName, setSelectedCampaignName, setFrom, setTo } = props;

    const { campaigns, channels } = useSelector((state: State) => state);
    const allOption: SimplePopoverPlainOption = { label: t('all'), value: '' };

    const mobileFiltersApply = (filtersObj, momentFrom, momentTo): void => {
        const dateValue = filtersObj[0];
        const campaignValue = filtersObj[1];
        let from;
        let to = dayjs().utc();

        switch (dateValue) {
            case DateDropdownOptions.Calendar:
                from = dayjs(momentFrom).startOf('day').utc().format();
                to = dayjs(momentTo).utc();
                break;
            case DateDropdownOptions.All:
                from = null;
                break;
            default:
                from = dayjs().subtract(dateValue, 'd').startOf('day').utc().format();
                break;
        }

        const urlQueryString = {
            ...(from ? { from: dayjs(from).format(SHORT_DATE_FORMAT) } : {}),
            to: dayjs(to).format(SHORT_DATE_FORMAT),
            ...(!!campaignValue ? { channelName: campaignValue } : {}),
        };
        dispatch(replace({ search: stringify(urlQueryString as any) }));

        setTo(to ? to.format() : null);
        setFrom(from);
        setSelectedCampaignName(campaignValue as string);
    };

    const dateRangeValue = useDateDropdownValue(true);
    const filterTabs: {
        label: string;
        type: 'checkbox' | 'radio' | 'date';
        defaultValue?: string;
        startDate?: string;
        endDate?: string;
        options: Record<string, any>[];
        hasSearch?: boolean;
        searchValue?: string;
    }[] = [
        {
            label: t('daterange'),
            defaultValue: dateRangeValue as string,
            options: useDateDropdownOptions(),
            startDate: from,
            endDate: to,
            type: 'date',
        },
        {
            defaultValue: selectedCampaignName || selectedChannelName,
            label: t('channels_campaigns'),
            options: [
                allOption,
                ...campaignsOptions(campaigns, t('loading'), t('results'), isMobile),
                ...channelsOptions(channels, t('loading'), t('results'), isMobile),
            ],
            type: 'radio',
            hasSearch: true,
        },
    ];

    return (
        <MobileFiltersContainer>
            <FiltersPopover
                onApply={(obj, momentFrom, momentTo): void => {
                    mobileFiltersApply(obj, momentFrom, momentTo);
                }}
                tabs={filterTabs}
                title={t('filters')}
                translations={{
                    apply: t('apply'),
                    close: t('close'),
                }}
            >
                <FilterIcon color={palette.gray[6]} width={20} height={20} />
            </FiltersPopover>
        </MobileFiltersContainer>
    );
};

export default MobileFiltersPopover;
